import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { SharedModule } from 'primeng/api';
import { NgIf } from '@angular/common';
import { MultiSelectModule } from 'primeng/multiselect';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { DataLayerType } from '../../../models/data-layer-type';
import { GlobalsStore } from '../../../stores/globals.state';
import { DataLayerTypeNameComponent } from '../data-layer-type-name/data-layer-type-name.component';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { DialogModule } from 'primeng/dialog';
import { HeaderComponent } from '../header/header.component';
import { DataLayerTypeFormComponent } from '../../organisms/data-layer-type-form/data-layer-type-form.component';
import { Button } from 'primeng/button';
import { PermissionService } from '../../../services/permission.service';

@Component({
  selector: 'app-type-dropdown',
  standalone: true,
  imports: [
    DropdownModule,
    ReactiveFormsModule,
    SharedModule,
    FormsModule,
    NgIf,
    MultiSelectModule,
    DataLayerTypeNameComponent,
    DialogModule,
    HeaderComponent,
    DataLayerTypeFormComponent,
    Button,
  ],
  templateUrl: './type-dropdown.component.html',
  styleUrl: './type-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TypeDropdownComponent),
      multi: true,
    },
  ],
})
export class TypeDropdownComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  readonly globalStore = inject(GlobalsStore);
  readonly dataStore = inject(IndicatorDataStore);
  readonly layerTypeStore = inject(DataLayerTypeStore);

  touched = false;
  disabled = false;

  @Input() enableEmpty = false;
  @Input() label = 'Räume/ Standorte';
  @Input() name = 'type';
  @Input() displayNewLayerTypeOption = false;
  @Input() globalSelector = false;
  @Input() mode: 'all' | 'Point' | 'Polygon' = 'Polygon';
  @Input() selection: DataLayerType | null = null;
  @Input() withData = false;
  @Input() withIndicators?: string[];
  @Input() noTypesAvailableMsg = 'keine Räume gefunden';
  @Output() selectionChange = new EventEmitter<DataLayerType>();

  addNewLayerTypeDialog = false;
  typeSelectItems: DataLayerType[] = [];
  canAdd = false;

  constructor(protected permissionService: PermissionService) {}

  /**
   * Lifecycle hook that is called when any data-bound input property changes.
   *
   * @param {SimpleChanges} changes - An object that contains the current and previous values of the changed properties.
   * @return {void} Does not return any value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selection']) {
      this.hasAddAccess();
    }
    if (changes['withIndicators'] && !changes['withIndicators'].firstChange) {
      this.typeSelectItems = this.getTypeSelectItems();
    }
  }

  /**
   * Angular lifecycle hook that is called after the component's data-bound properties are initialized.
   * This method initializes necessary data and state for the component. Specifically, it checks for
   * add access permissions and sets up the type selection items required by the component.
   *
   * @return {void} No return value.
   */
  ngOnInit(): void {
    this.hasAddAccess();
    this.typeSelectItems = this.getTypeSelectItems();
  }

  /**
   * Determines whether the user has the necessary permissions to add items
   * based on the current selection and the associated rights in the data layer.
   *
   * @return {void} This method does not return a value, but updates the `canAdd` property
   *                with a boolean indicating if the user has the required add permissions.
   */
  public hasAddAccess(): void {
    this.canAdd = this.selection
      ? this.permissionService.checkDataLayerRights(
          this.selection._id,
          ['add'],
          false
        )
      : false;
  }

  /**
   * Retrieves a list of data layer types based on the specified mode and configured filters.
   *
   * Depending on the mode, it fetches specific types (e.g., 'Polygon' or 'Point') or all available types.
   * Additional filters such as `withIndicators` or `withData` are applied if configured.
   *
   * @return {DataLayerType[]} A filtered list of data layer types, which could be empty if no types match the criteria.
   */
  getTypeSelectItems(): DataLayerType[] {
    let types: DataLayerType[] = [];
    switch (this.mode) {
      case 'Polygon':
        types = this.layerTypeStore.getByType('Polygon');
        break;
      case 'Point':
        types = this.layerTypeStore.getByType('Point');
        break;
      case 'all':
        types = this.layerTypeStore.typeSorted();
        break;
    }

    if (this.withIndicators && types) {
      const indicatorIds = this.withIndicators;
      return types.filter(type =>
        this.dataStore.dataLayerTypeAndIndicatorsHasData(type._id, indicatorIds)
      );
    }

    if (this.withData && types) {
      return types.filter(type =>
        this.dataStore.dataLayerTypeHasData(type._id)
      );
    }

    return types || [];
  }

  /**
   * Handles change events for a specified data layer. This function is
   * triggered whenever the data layer selection is modified and performs
   * a delayed emission of the updated value. Additionally, it updates
   * the selected type in the global store if applicable.
   *
   * @param {DataLayerType | DataLayerType[] | undefined} value - The new
   *        value for the data layer selection. Can be a single data
   *        layer type, an array of data layer types, or undefined.
   * @returns {void} This function does not return a value.
   */
  protected onChange = (
    value: DataLayerType | DataLayerType[] | undefined
  ): void => {
    setTimeout(() => {
      this.selectionChange.emit(value as DataLayerType);

      if (this.globalSelector && value)
        this.globalStore.setSelectedType(value as DataLayerType);
    }, 300);
  };

  private onTouched = (): void => {
    this.touched = false;
  };

  /**
   * Updates the selection property with the given value.
   *
   * @param {any} value - The value to be written to the selection.
   * @return {void}
   */
  writeValue(value: any): void {
    this.selection = value;
  }

  /**
   * Registers a change callback function that will be called when changes occur.
   *
   * This function is typically used to provide a custom change handler that will be invoked with the updated value.
   *
   * @param fn The callback function to register for handling changes. It should accept the updated value as its argument.
   * @return void
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function to be invoked when the control is touched by the user.
   *
   * @param {Function} fn - The callback function that will be executed on touch event.
   * @return {void} This method does not return a value.
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state of the component.
   *
   * @param {boolean} isDisabled - A boolean value indicating whether the component should be disabled.
   * @return {void} Does not return any value.
   */
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Opens the form dialog for adding a new layer type.
   * Sets the `addNewLayerTypeDialog` property to true, making the dialog visible.
   *
   * @return {void} Does not return a value.
   */
  openLayerTypeForm(): void {
    this.addNewLayerTypeDialog = true;
  }
}
